import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import TawkChat from "@/components/TawkChat";
import BrandLogo from "@/components/BrandLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import { COUNTRY_META } from "@/lib/iban";
import {
  LayoutGrid, ArrowLeftRight, ListOrdered, FileText, BadgeCheck,
  UserRound, LogOut, Menu, X, ShieldAlert, Users, BarChart3,
} from "lucide-react";

const items = [
  { to: "/app",                  i: LayoutGrid,     k: "sidebar.overview",   end: true, tid: "nav-overview" },
  { to: "/app/transferencias",   i: ArrowLeftRight, k: "sidebar.transfers", tid: "nav-transfers" },
  { to: "/app/movimientos",      i: ListOrdered,    k: "sidebar.movements", tid: "nav-movements" },
  { to: "/app/documentos",       i: FileText,       k: "sidebar.documents", tid: "nav-documents" },
  { to: "/app/kyc",              i: BadgeCheck,     k: "sidebar.kyc",       tid: "nav-kyc" },
  { to: "/app/perfil",           i: UserRound,      k: "sidebar.profile",   tid: "nav-profile" },
];

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const isAdmin = user?.role === "admin";

  const initials = (user?.full_name || "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "U";
  const countryFlag = COUNTRY_META[(user?.country || "").toUpperCase()]?.flag || "";

  useEffect(() => {
    let alive = true;
    if (!isAdmin) { setPendingCount(0); return; }
    const fetchStats = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get("/admin/transfers/stats");
        if (alive) setPendingCount(data.pending || 0);
      } catch { /* ignore */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    const onVisible = () => { if (!document.hidden) fetchStats(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [isAdmin]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-layout">
      <TawkChat />
      {/* Topbar */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="px-4 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 -ml-1" onClick={() => setOpen((s) => !s)} data-testid="mobile-menu-toggle" aria-label="Menú">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <Link to="/" className="flex items-center" data-testid="dash-logo">
              <BrandLogo size={26} />
            </Link>
            <span className="hidden md:block text-[11px] font-mono-fin text-slate-500 ml-3 border-l border-slate-200 pl-3">
              {t("sidebar.panel_version")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <LanguageSwitcher tone="dark" compact />
            <div className="hidden md:flex items-center gap-2.5">
              <div className="relative" data-testid="user-avatar">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                     style={{ background: "linear-gradient(135deg,#6D28D9,#2563EB)" }}>
                  {initials}
                </div>
                {countryFlag && (
                  <span className="absolute -bottom-1 -right-1 text-sm leading-none drop-shadow" data-testid="user-flag">{countryFlag}</span>
                )}
              </div>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-sm font-semibold text-slate-900">{user?.full_name}</span>
                <span className="text-[11px] font-mono-fin text-slate-500">{user?.email}</span>
              </div>
            </div>
            <div className="hidden lg:block">
              <button onClick={() => setConfirmLogout(true)} className="btn-secondary text-sm" data-testid="logout-btn">
                <LogOut size={16} /> {t("sidebar.logout")}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-14 left-0 z-30 h-[calc(100vh-3.5rem)] w-64 bg-white border-r border-slate-200
                      ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform`}
          data-testid="sidebar"
        >
          <nav className="p-3 space-y-1">
            {items.map(({ to, i: I, k, end, tid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                    isActive ? "text-white font-semibold shadow-[0_10px_25px_-8px_rgba(124,58,237,0.55)]" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                style={({ isActive }) => isActive ? { background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #2563EB 100%)" } : {}}
              >
                <I size={17} /> {t(k)}
              </NavLink>
            ))}

            {isAdmin && (
              <>
                <div className="pt-3 mt-2 border-t border-slate-100">
                  <div className="px-4 py-1 text-[10px] font-mono-fin uppercase tracking-widest text-slate-400">{t("sidebar.admin")}</div>
                </div>
                <NavLink
                  to="/app/admin/aprobaciones"
                  onClick={() => setOpen(false)}
                  data-testid="nav-admin-approvals"
                  className={({ isActive }) =>
                    `flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                      isActive ? "text-white font-semibold shadow-[0_10px_25px_-8px_rgba(220,38,38,0.45)]" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: "linear-gradient(135deg, #DC2626 0%, #7C3AED 60%, #2563EB 100%)" } : {}}
                >
                  <span className="flex items-center gap-3"><ShieldAlert size={17} /> {t("sidebar.approvals")}</span>
                  {pendingCount > 0 && (
                    <span
                      data-testid="admin-pending-badge"
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold font-mono-fin"
                    >
                      {pendingCount}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/app/admin/usuarios"
                  onClick={() => setOpen(false)}
                  data-testid="nav-admin-users"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                      isActive ? "text-white font-semibold shadow-[0_10px_25px_-8px_rgba(124,58,237,0.55)]" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)" } : {}}
                >
                  <Users size={17} /> {t("sidebar.users")}
                </NavLink>
                <NavLink
                  to="/app/admin/metricas"
                  onClick={() => setOpen(false)}
                  data-testid="nav-admin-metrics"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                      isActive ? "text-white font-semibold shadow-[0_10px_25px_-8px_rgba(124,58,237,0.55)]" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: "linear-gradient(135deg, #4F46E5 0%, #9333EA 100%)" } : {}}
                >
                  <BarChart3 size={17} /> {t("sidebar.metrics")}
                </NavLink>
              </>
            )}

            {/* Logout — shown inside the menu on mobile (topbar logout is desktop-only) */}
            <button
              onClick={() => { setOpen(false); setConfirmLogout(true); }}
              data-testid="sidebar-logout"
              className="lg:hidden w-full flex items-center gap-3 px-4 py-2.5 mt-2 pt-3 border-t border-slate-100 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-all"
            >
              <LogOut size={17} /> {t("sidebar.logout")}
            </button>
          </nav>
          <div className="absolute bottom-3 left-3 right-3 rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">{t("sidebar.kyc_status")}</div>
            <div className={`mt-1 inline-block px-2.5 py-0.5 rounded-full text-xs font-mono-fin ${
              user?.kyc_status === "verified" ? "bg-green-100 text-green-700" :
              user?.kyc_status === "verification_required" ? "bg-orange-100 text-orange-700" :
              "bg-amber-100 text-amber-700"
            }`} data-testid="kyc-status-pill">
              {user?.kyc_status === "verified" ? t("sidebar.kyc_verified") :
               user?.kyc_status === "verification_required" ? t("sidebar.kyc_required") :
               t("sidebar.kyc_pending")}
            </div>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-20 bg-slate-900/30 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 lg:p-8 pb-28 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 min-h-[calc(100vh-3.5rem)]">
          <Outlet />
        </main>
      </div>

      {confirmLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
             onClick={() => setConfirmLogout(false)} data-testid="logout-confirm-modal">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-red-100 flex items-center justify-center shrink-0">
                <LogOut className="text-red-600" size={22} />
              </div>
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">{t("sidebar.logout_confirm_title")}</h3>
                <p className="text-sm text-slate-500 mt-1">{t("sidebar.logout_confirm_sub")}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmLogout(false)} data-testid="logout-cancel-btn"
                className="btn-secondary text-sm">{t("sidebar.logout_cancel")}</button>
              <button onClick={() => { setConfirmLogout(false); doLogout(); }} data-testid="logout-confirm-btn"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-all">
                <LogOut size={16} /> {t("sidebar.logout_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
